import React, { useState } from 'react';

const SUGGESTIONS = [
  'Dentists',
  'Restaurants',
  'Plumbers',
  'Roofers',
  'Gyms & fitness',
  'Real estate agents',
  'Law firms',
  'Auto repair',
  'Salons & spas',
  'Contractors',
  'Medical clinics',
  'Hotels',
];

const MAX_SELECTED = 3;
const MAX_LENGTH = 100;

interface BusinessTypeChipPickerProps {
  value: string[];
  onChange: (next: string[]) => void;
}

const BusinessTypeChipPicker: React.FC<BusinessTypeChipPickerProps> = ({ value, onChange }) => {
  const [draft, setDraft] = useState('');
  const atCap = value.length >= MAX_SELECTED;

  const commit = (raw: string) => {
    const trimmed = raw.trim().slice(0, MAX_LENGTH);
    if (!trimmed || atCap) {
      setDraft('');
      return;
    }
    const isDuplicate = value.some(v => v.toLowerCase() === trimmed.toLowerCase());
    if (isDuplicate) {
      setDraft('');
      return;
    }
    onChange([...value, trimmed]);
    setDraft('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
    }
  };

  const removeChip = (chip: string) => {
    onChange(value.filter(v => v !== chip));
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {SUGGESTIONS.map(suggestion => {
          const isSelected = value.some(v => v.toLowerCase() === suggestion.toLowerCase());
          const isDisabled = !isSelected && atCap;
          return (
            <button
              key={suggestion}
              type="button"
              disabled={isDisabled}
              onClick={() => commit(suggestion)}
              className={[
                'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
                isSelected
                  ? 'border-primary bg-primary/10 text-primary'
                  : isDisabled
                  ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                  : 'border-gray-200 text-gray-600 hover:border-primary/40 hover:text-secondary',
              ].join(' ')}
            >
              {suggestion}
            </button>
          );
        })}
      </div>

      <input
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={atCap}
        placeholder={atCap ? '3 is the maximum' : 'Or type a business type'}
        className={[
          'w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition',
          atCap ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-60' : 'border-gray-300',
        ].join(' ')}
      />

      {atCap && <p className="text-xs text-amber-600 mt-2">3 is the maximum.</p>}

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {value.map(chip => (
            <span
              key={chip}
              className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-full text-sm font-medium bg-primary text-white"
            >
              {chip}
              <button
                type="button"
                onClick={() => removeChip(chip)}
                aria-label={`Remove ${chip}`}
                className="hover:bg-white/20 rounded-full p-0.5"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default BusinessTypeChipPicker;
